import React, { useEffect, useMemo, useCallback } from 'react'
import { Alert, Divider, TreeSelect, Space, Tag, theme, Button } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, CheckOutlined, CloseOutlined, UpOutlined } from '@ant-design/icons'
import { isNil } from 'lodash'
import CreateConceptualLabelButton from '../forms/create/CreateConceptualLabelButton'
import { useThemeColors } from '../../../../hooks/useThemeColors'
import { useDispatch } from 'react-redux'
import { conceptualLabelActions } from '../../../../store/slices/conceptual-label.slice'
import AiIcon from '../../../../components/common/icons/AiIcon'
import { useParams } from 'react-router-dom'
import useConceptualLabelsByDiscipline from '../hooks/useConceptualLabelsByDiscipline'
import { ConceptualLabelScopes } from '../../../../constants/conceptual-label-scopes'

export const SuggestedTag = (
    <Tag icon={<AiIcon />} color='blue' bordered={false} className='m-0'>
        Suggested
    </Tag>
)

// Constants for temporary ID generation
const TEMP_ID_PREFIX = 'temp-'
const generateTempId = index => `${TEMP_ID_PREFIX}${index}`
export const isTemporaryId = id => String(id).startsWith(TEMP_ID_PREFIX)

// Utility functions for data transformation
export const denormalizeConceptualLabelsMatches = items => {
    return items.map(item => ({
        ...item,
        id: isTemporaryId(item.id) ? null : item.id,
    }))
}

export const normalizeConceptualLabelsMatches = labels => {
    if (!labels || !Array.isArray(labels)) return []

    return labels.map(label => {
        if (label && typeof label === 'object' && 'id' in label) {
            return {
                ...label,
                id: label.id,
                disposition: label.disposition ?? true,
            }
        }

        return { id: label, disposition: true }
    })
}

// Custom hook for proposed conceptual labels management
const useProposedConceptualLabels = proposedConceptualLabels => {
    const proposedLabelsMap = useMemo(() => {
        const map = {}

        proposedConceptualLabels.forEach((cl, index) => {
            const id = isNil(cl.id) ? generateTempId(index) : cl.id
            map[id] = {
                ...cl,
                id,
                isProposed: isTemporaryId(id),
            }
        })

        return map
    }, [proposedConceptualLabels])

    const defaultInitialValue = useMemo(() => {
        if (proposedConceptualLabels.length > 0) {
            return proposedConceptualLabels.map((cl, index) => ({
                ...cl,
                id: isNil(cl.id) ? generateTempId(index) : cl.id,
                disposition: cl.disposition ?? true,
            }))
        }
        return []
    }, [proposedConceptualLabels])

    return { proposedLabelsMap, defaultInitialValue }
}

// Custom hook for tree data building
const useTreeData = (conceptualLabelsData, proposedLabelsMap) => {
    return useMemo(() => {
        const labelMap = {}

        // Helper function to insert proposed CL into correct tree position
        const insertProposedCLIntoTree = (treeNodes, proposedCL) => {
            if (!proposedCL.disciplines?.[0]) {
                return treeNodes
            }

            const disciplineId = proposedCL.disciplines[0].id
            const disciplineNode = treeNodes.find(node => node.key?.toString() === disciplineId.toString())

            if (!disciplineNode) {
                return treeNodes
            }

            const proposedNode = {
                title: proposedCL.name || 'Untitled',
                value: proposedCL.id,
                key: proposedCL.id,
                isProposed: true,
            }

            if (proposedCL.parent_id) {
                const insertIntoChildren = (children = []) => {
                    for (let child of children) {
                        if (child.value === proposedCL.parent_id) {
                            child.children = child.children || []
                            child.children.push(proposedNode)
                            return children
                        }

                        if (child.children) {
                            insertIntoChildren(child.children)
                        }
                    }
                    return [...children, proposedNode]
                }

                disciplineNode.children = insertIntoChildren(disciplineNode.children)
            } else {
                disciplineNode.children = disciplineNode.children || []
                disciplineNode.children.push(proposedNode)
            }

            return treeNodes
        }

        // Transform existing conceptual labels to tree structure
        const transformToTreeData = (items, parentKey = '') => {
            return (
                items?.map(item => {
                    const key = parentKey ? `${parentKey}-${item.id}` : item.id

                    if (item.isDiscipline) {
                        return {
                            title: item.visualId + ' - ' + item.name,
                            value: `discipline-${key}`,
                            key: `discipline-${key}`,
                            selectable: false,
                            children: transformToTreeData(item.children || [], key),
                        }
                    } else {
                        labelMap[item.id] = item
                        return {
                            title: item.visualId + ' - ' + item.name,
                            value: item.id,
                            key: item.id,
                            isProposed: isTemporaryId(item.id),
                            children: transformToTreeData(item.children || [], key),
                        }
                    }
                }) || []
            )
        }

        // Build tree with existing data
        let treeData = transformToTreeData(conceptualLabelsData)

        // Insert proposed CLs into appropriate tree positions
        Object.values(proposedLabelsMap).forEach(cl => {
            if (!labelMap[cl.id] || isTemporaryId(cl.id)) {
                labelMap[cl.id] = cl
                treeData = insertProposedCLIntoTree(treeData, cl)
            }
        })

        return { treeData, labelMap }
    }, [conceptualLabelsData, proposedLabelsMap])
}

// Custom hook for initialization logic
const useConceptualLabelInitialization = (
    inputValue,
    initialValue,
    defaultInitialValue,
    proposedConceptualLabels,
    onChange,
) => {
    useEffect(() => {
        if (!inputValue && (initialValue || defaultInitialValue.length > 0) && onChange) {
            let valueToSet

            if (initialValue) {
                const normalizedInitial = normalizeConceptualLabelsMatches(initialValue)

                if (proposedConceptualLabels.length > 0) {
                    valueToSet = normalizedInitial.map(item => {
                        if (item.id === null) {
                            const proposedIndex = proposedConceptualLabels.findIndex(
                                proposed => proposed.name === item.name && proposed.description === item.description,
                            )

                            if (proposedIndex !== -1) {
                                return {
                                    ...item,
                                    id: generateTempId(proposedIndex),
                                }
                            }
                        }
                        return item
                    })
                } else {
                    valueToSet = normalizedInitial
                }
            } else {
                valueToSet = defaultInitialValue
            }

            onChange(valueToSet)
        }
    }, [initialValue, defaultInitialValue, inputValue, onChange, proposedConceptualLabels])
}

const ConceptualLabelSelect = ({
    value: inputValue,
    onChange,
    disabled,
    initialValue,
    proposedConceptualLabels = [],
    scope = ConceptualLabelScopes.ALL,
    ...props
}) => {
    const colors = useThemeColors()
    const { token: themeToken } = theme.useToken()
    const { token } = useParams()
    const dispatch = useDispatch()

    const {
        data: conceptualLabelsData,
        isFetching: isFetchingConceptualLabels,
        error: conceptualLabelsError,
    } = useConceptualLabelsByDiscipline({
        scope,
        token: token,
    })

    // Custom hooks for cleaner logic separation
    const { proposedLabelsMap, defaultInitialValue } = useProposedConceptualLabels(proposedConceptualLabels)
    const { treeData } = useTreeData(conceptualLabelsData, proposedLabelsMap)

    // Normalize and initialize value
    const value = normalizeConceptualLabelsMatches(inputValue)
    useConceptualLabelInitialization(inputValue, initialValue, defaultInitialValue, proposedConceptualLabels, onChange)

    // Get selected IDs for TreeSelect
    const selectedIds = value.map(item => item.id)

    // Event handlers
    const handleDispositionChange = useCallback(
        (id, newDisposition) => {
            if (!onChange) return

            const newValue = value.map(item => (item.id === id ? { ...item, disposition: newDisposition } : item))

            onChange(newValue)
        },
        [value, onChange],
    )

    const handleTreeSelectChange = useCallback(
        newIds => {
            if (!onChange) return

            const existingItems = value.filter(item => newIds.includes(item.id))
            const existingIds = existingItems.map(item => item.id)

            const newItems = newIds
                .filter(id => !existingIds.includes(id))
                .map(id => {
                    const baseItem = { id, disposition: true }

                    if (isTemporaryId(id) && proposedLabelsMap[id]) {
                        return {
                            ...baseItem,
                            ...proposedLabelsMap[id],
                        }
                    }

                    return baseItem
                })

            onChange([...existingItems, ...newItems])
        },
        [value, onChange, proposedLabelsMap],
    )

    // Render functions
    const tagRender = useCallback(
        props => {
            const { label, value: id, closable, onClose } = props
            const conceptualLabelMatch = value.find(item => item.id === id)

            const onPreventMouseDown = event => {
                event.preventDefault()
                event.stopPropagation()
            }

            const isChecked = conceptualLabelMatch?.disposition

            const handleIconClick = e => {
                e.stopPropagation()
                if (disabled) return
                handleDispositionChange(id, !isChecked)
            }

            const handleLabelClick = e => {
                e.stopPropagation()
                if (!isTemporaryId(id)) {
                    dispatch(conceptualLabelActions.setEditingConceptualLabelId(id))
                }
            }

            const handleDeleteClick = e => {
                e.stopPropagation()
                if (disabled) return
                const newValue = value.filter(item => item.id !== id)
                onChange(newValue)
            }

            return (
                <Tag.CheckableTag
                    checked={isChecked}
                    color={id}
                    onMouseDown={onPreventMouseDown}
                    closable={closable}
                    onClose={onClose}
                    style={{
                        marginInlineEnd: 4,
                        background: 'transparent',
                        color: colors.text,
                        borderColor: themeToken.colorBorder,
                    }}
                >
                    <Space size={4}>
                        <Button
                            title={isChecked ? 'Disposition: positive' : 'Disposition: negative'}
                            type='text'
                            size='small'
                            icon={
                                isChecked ? (
                                    <ArrowUpOutlined style={{ color: colors.success }} />
                                ) : (
                                    <ArrowDownOutlined style={{ color: colors.error }} />
                                )
                            }
                            onClick={handleIconClick}
                        />
                        <span
                            onClick={handleLabelClick}
                            style={{
                                cursor: isTemporaryId(id) ? 'default' : 'pointer',
                            }}
                        >
                            {label}
                        </span>
                        <Button
                            title='Remove Label'
                            type='text'
                            size='small'
                            icon={<CloseOutlined />}
                            onClick={handleDeleteClick}
                            disabled={disabled}
                        />
                    </Space>
                </Tag.CheckableTag>
            )
        },
        [value, disabled, themeToken.colorBorder, handleDispositionChange, onChange, dispatch],
    )

    const treeTitleRender = useCallback(nodeData => {
        return (
            <Space>
                {nodeData.title}
                {nodeData.isProposed && SuggestedTag}
            </Space>
        )
    }, [])

    // Calculate expanded keys for selected items
    const treeDefaultExpandedKeys = useMemo(() => {
        if (!selectedIds || selectedIds.length === 0) return []

        const nodeParentMap = {}

        const buildParentMap = (nodes, parentKey = null) => {
            if (!nodes) return

            nodes.forEach(node => {
                if (parentKey) {
                    nodeParentMap[node.key] = parentKey
                }

                if (node.children?.length > 0) {
                    buildParentMap(node.children, node.key)
                }
            })
        }

        buildParentMap(treeData)

        const parentKeys = new Set()
        selectedIds.forEach(id => {
            let currentKey = id
            while (nodeParentMap[currentKey]) {
                const parentKey = nodeParentMap[currentKey]
                parentKeys.add(parentKey)
                currentKey = parentKey
            }
        })

        return Array.from(parentKeys)
    }, [selectedIds, treeData])

    return (
        <>
            <TreeSelect
                treeData={treeData}
                value={selectedIds}
                onChange={handleTreeSelectChange}
                allowClear
                style={{ width: '100%' }}
                loading={isFetchingConceptualLabels}
                showSearch
                multiple
                treeNodeFilterProp='title'
                tagRender={tagRender}
                treeTitleRender={treeTitleRender}
                treeDefaultExpandedKeys={treeDefaultExpandedKeys}
                disabled={disabled}
                variant={disabled ? 'borderless' : undefined}
                placeholder='Please select'
                dropdownRender={menu => (
                    <>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <CreateConceptualLabelButton type='text' />
                    </>
                )}
                getPopupContainer={triggerNode => triggerNode.parentNode}
                popupMatchSelectWidth={false}
                {...props}
            />
            {conceptualLabelsError && <Alert message={conceptualLabelsError} type='error' showIcon closable />}
        </>
    )
}

export default ConceptualLabelSelect
